import { supabase, getSupabaseClient } from './supabaseClient';

/**
 * Sign up with email and password
 */
export async function signUpWithEmail(email, password) {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      emailRedirectTo: `${window.location.origin}/`,
    },
  });

  if (error) {
    console.error('Error signing up:', error);
    throw error;
  }

  return data;
}

/**
 * Sign in with email and password
 */
export async function signInWithEmail(email, password) {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (error) {
    console.error('Error signing in:', error);
    throw error;
  }

  return data;
}

/**
 * Send password reset email
 */
export async function resetPassword(email) {
  const client = getSupabaseClient();

  const { data, error } = await client.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`,
  });

  if (error) {
    // Log full error details for debugging
    console.error('Error sending password reset:', {
      message: error.message,
      status: error.status,
      code: error.code,
      name: error.name,
      fullError: error,
    });

    // Handle rate limiting (HTTP 429 or error code 429)
    if (error.status === 429 || error.code === 429) {
      throw new Error('Too many password reset requests. Please wait a few minutes before trying again.');
    }

    // Handle error 1101 - typically email sending issues or rate limiting
    // Supabase error codes can appear in message, code, or status
    const errorString = JSON.stringify(error).toLowerCase();
    const messageString = (error.message || '').toLowerCase();

    if (
      error.code === 1101 ||
      error.code === '1101' ||
      error.status === 1101 ||
      messageString.includes('1101') ||
      errorString.includes('1101')
    ) {
      throw new Error(
        'Unable to send password reset email. This may be due to rate limiting or email service configuration. ' +
        'Please wait a few minutes and try again, or contact support if the issue persists.'
      );
    }

    // Handle rate limit messages
    if (
      messageString.includes('rate limit') ||
      messageString.includes('too many') ||
      messageString.includes('email rate limit')
    ) {
      throw new Error('Too many password reset requests. Please wait a few minutes before trying again.');
    }

    // Handle email not confirmed
    if (messageString.includes('email not confirmed')) {
      throw new Error('Please verify your email address first before resetting your password.');
    }

    // Handle email not found (Supabase usually doesn't reveal this for security)
    if (messageString.includes('user not found') || messageString.includes('email not found')) {
      throw new Error('If an account exists with this email, you will receive a password reset link.');
    }

    // Handle SMTP/email configuration issues
    if (
      messageString.includes('smtp') ||
      messageString.includes('email provider') ||
      messageString.includes('email service')
    ) {
      throw new Error(
        'Email service is temporarily unavailable. Please try again later or contact support.'
      );
    }

    // Default: throw the original error with a user-friendly wrapper
    throw new Error(error.message || 'Failed to send password reset email. Please try again.');
  }

  return data;
}

/**
 * Update user password (used after reset)
 */
export async function updatePassword(newPassword) {
  const client = getSupabaseClient();

  const { data, error } = await client.auth.updateUser({
    password: newPassword,
  });

  if (error) {
    console.error('Error updating password:', error);

    // Handle specific password update errors
    if (error.message?.includes('same as old')) {
      throw new Error('New password must be different from your current password.');
    }

    throw error;
  }

  return data;
}

/**
 * Sign out the current user
 */
export async function signOut() {
  const { error } = await supabase.auth.signOut();

  if (error) {
    console.error('Error signing out:', error);
    throw error;
  }
}

/**
 * Get the current session
 */
export async function getSession() {
  const { data: { session }, error } = await supabase.auth.getSession();

  if (error) {
    console.error('Error getting session:', error);
    throw error;
  }

  return session;
}

/**
 * Get the current user
 */
export async function getCurrentUser() {
  const { data: { user }, error } = await supabase.auth.getUser();

  if (error) {
    console.error('Error getting user:', error);
    throw error;
  }

  return user;
}

/**
 * Subscribe to auth state changes
 */
export function onAuthStateChange(callback) {
  const { data: { subscription } } = supabase.auth.onAuthStateChange(
    (event, session) => {
      callback(event, session);
    }
  );

  return subscription;
}
